import React, { useState, useEffect } from 'react';
import { useAuthStore, useProductStore } from './store';
import { authAPI } from './api/client';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import ClothingPage from './pages/ClothingPage';
import PendingPage from './pages/PendingPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import SheetViewTab from './components/SheetViewTab';
import ConfirmDialog from './components/ConfirmDialog';
import { useTheme } from './hooks/useTheme';
import './styles/global.css';

function App() {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('login'); // 'login' | 'pending' | 'catalog' | 'admin' | 'settings'
  const [prevPage, setPrevPage] = useState('catalog');
  const [section, setSection] = useState(() => localStorage.getItem('section') || 'shoes');
  const switchSection = (s) => { setSection(s); localStorage.setItem('section', s); };
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  useTheme();
  const token = useAuthStore((state) => state.token);
  const setUser = useAuthStore((state) => state.setUser);
  const user = useAuthStore((state) => state.user);
  const canSwitchSection = !!(user && (user.is_admin || user.is_team || user.subscription_type === 'Обувь и Одежда'));
  // Юзери з retail-правами але без підписки каталогу — не мають доступу до catalog-сторінок
  const hasCatalogAccess = !!(user && (user.is_admin || user.is_team || user.subscription_type));

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
      setPage('login');
    }
  }, [token]);

  const checkAuth = async (attempt = 0) => {
    try {
      const res = await authAPI.getMe();
      const user = res.data;
      setUser(user);
      if (user.subscription_type === 'Одежда') setSection('clothing');
      const hasSku = new URLSearchParams(window.location.search).has('sku');
      const lastPage = sessionStorage.getItem('last_page');
      const perms = user.permissions || [];
      const hasAdminPanel = perms.some(p => ['retail_sites', 'retail_publish', 'retail_site_admin',
        'queue', 'analytics', 'selection', 'photo_fix', 'sheet_view'].includes(p));
      const wantSheet = new URLSearchParams(window.location.search).has('sheet');
      if (!user.is_active) {
        setPage('pending');
      } else if (wantSheet && (user.is_admin || perms.includes('sheet_view'))) {
        // Пряме посилання ?sheet=1 — таблиця одразу на весь екран
        setPage('sheet');
      } else if (user.is_admin && !hasSku && lastPage !== 'catalog') {
        // Адмін: іде в панель якщо явно не вибрав каталог
        setPage('admin');
      } else if (hasAdminPanel && !hasSku) {
        // Не-адмін з правами: завжди в панель (вони не мають доступу до каталогу)
        setPage('admin');
      } else {
        setPage('catalog');
      }
      setLoading(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Настоящий выброс — сессия недействительна
        useAuthStore.getState().logout();
        setPage('login');
        setLoading(false);
      } else {
        // Сетевая ошибка или 5xx — сервер перезапускается, ждём и повторяем
        // Показываем спиннер (loading=true) пока сервер не поднимется
        const delay = attempt < 5 ? 3000 : 8000;
        setTimeout(() => checkAuth(attempt + 1), delay);
      }
    }
  };

  // Сохраняем текущую страницу чтобы после обновления вернуть пользователя на то же место
  useEffect(() => {
    if (page === 'catalog' || page === 'admin') {
      sessionStorage.setItem('last_page', page);
    }
  }, [page]);

  // Якщо юзер без доступу до каталогу якимось чином потрапив на catalog-сторінку — повертаємо в admin
  useEffect(() => {
    if (page === 'catalog' && user && !hasCatalogAccess) {
      const perms = user.permissions || [];
      const hasAdminAccess = user.is_admin || perms.some(p =>
        ['retail_sites','retail_publish','retail_site_admin','queue','analytics','selection','photo_fix','sheet_view'].includes(p)
      );
      if (hasAdminAccess) {
        setPage('admin');
      } else {
        setPage('no_access');
      }
    }
  }, [page, user, hasCatalogAccess]);

  // Навигация из Cheese → сразу открыть карточку товара (тот же механизм,
  // что и у прямых ссылок ?sku= — HomePage сам подхватит из sessionStorage
  // при монтировании). Фильтр поиска оставляем как фолбэк — если товар
  // не найдётся по SKU (например, ещё не в каталоге), список хотя бы
  // отфильтруется по этому артикулу вместо пустого каталога.
  useEffect(() => {
    const handler = (e) => {
      const { sku } = e.detail || {};
      if (sku) {
        sessionStorage.setItem('open_product_sku', sku);
        useProductStore.getState().setFilters({ search: sku });
      }
      setPage('catalog');
    };
    window.addEventListener('cheese-open-catalog', handler);
    return () => window.removeEventListener('cheese-open-catalog', handler);
  }, []);

  // Heartbeat — обновляем last_seen; если сервер вернул новый токен — сохраняем.
  // Если сервер сообщил session_invalid (наш токен "осиротел" — сессию уже
  // сменили в другой вкладке/потоке) — сразу выходим с явным приглашением
  // войти заново, вместо того чтобы молча продолжать "успешные" heartbeat'ы
  // и словить непонятный вылет позже, при следующем реальном действии.
  const _heartbeat = () => {
    authAPI.heartbeat()
      .then(res => {
        const newToken = res?.data?.access_token;
        if (newToken) useAuthStore.getState().setToken(newToken);
        if (res?.data?.session_invalid) {
          useAuthStore.getState().logout();
          setPage('login');
        }
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (!token || page === 'login') return;
    _heartbeat();
    const interval = setInterval(_heartbeat, 60 * 1000);
    return () => clearInterval(interval);
  }, [token, page]);

  const handleLoginSuccess = () => checkAuth();

  const handleSettings = () => { setPrevPage(page); setPage('settings'); };
  const handleBackFromSettings = () => setPage(prevPage);

  const handleLogout = () => setShowLogoutConfirm(true);

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    try { await authAPI.logout(); } catch {}
    useAuthStore.getState().logout();
    setPage('login');
  };

  const handleStopImpersonate = async () => {
    try {
      await authAPI.stopImpersonate();
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch {}
  };

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <>
      {showLogoutConfirm && (
        <ConfirmDialog
          message="Вы действительно хотите выйти?"
          onConfirm={confirmLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
      {/* Банер режиму перегляду від імені іншого користувача */}
      {user?.impersonated_by && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#f59e0b', color: '#1c1c1e',
          padding: '7px 16px', display: 'flex', alignItems: 'center',
          gap: 12, fontSize: 13, fontWeight: 500, boxShadow: '0 2px 8px rgba(0,0,0,.2)',
        }}>
          <span>Перегляд від імені <strong>{user.email}</strong></span>
          <button
            onClick={handleStopImpersonate}
            style={{
              background: 'rgba(0,0,0,.18)', border: 'none', borderRadius: 6,
              padding: '3px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 12,
            }}
          >
            Вийти ×
          </button>
        </div>
      )}
      {page === 'login' && <LoginPage onLoginSuccess={handleLoginSuccess} />}
      {page === 'pending' && <PendingPage onLogout={handleLogout} />}
      {page === 'no_access' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      height: '100vh', gap: 16, color: 'var(--text)', background: 'var(--bg)' }}>
          <div style={{ fontSize: 48 }}>🔒</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Немає доступу</div>
          <div style={{ fontSize: 14, color: 'var(--muted,#888)', textAlign: 'center', maxWidth: 280 }}>
            У цього користувача немає доступу ні до каталогу, ні до панелі управління.
          </div>
          <button className="btn btn-secondary" onClick={handleLogout} style={{ marginTop: 8 }}>Вийти</button>
        </div>
      )}
      {page === 'sheet' && <SheetViewTab standalone />}
      {page === 'admin' && <AdminPage onLogout={handleLogout} onGoToCatalog={() => setPage('catalog')} onSettings={handleSettings} />}
      {page === 'catalog' && hasCatalogAccess && section === 'shoes' && (
        <HomePage
          onLogout={handleLogout}
          onAdmin={() => setPage('admin')}
          onSettings={handleSettings}
          onSwitchSection={switchSection}
          canSwitchSection={canSwitchSection}
        />
      )}
      {page === 'catalog' && hasCatalogAccess && section === 'clothing' && (
        <ClothingPage
          onLogout={handleLogout}
          onAdmin={() => setPage('admin')}
          onSettings={handleSettings}
          onSwitchSection={switchSection}
          canSwitchSection={canSwitchSection}
        />
      )}
{page === 'settings' && <SettingsPage onBack={handleBackFromSettings} onAdmin={() => setPage('admin')} />}
    </>
  );
}

export default App;
